import { Platform } from '@angular/cdk/platform';
import { AsyncPipe } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, computed, ElementRef, inject, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  ActivatedRoute,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterModule,
} from '@angular/router';
import { ConsultationOfferService } from '@rma/finder/entrypoints/consultation-offer';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { ShortlistService } from '@rma/public/compare-agent/private-api/da-compare-agent';
import { CompareTrayContainerComponent } from '@rma/public/compare-agent/public-api/compare-tray';
import { FeatureFlagService, TempFeatureFlags } from '@rma/public/generic/util/feature-flags';
import { WrongRegionModalService } from '@rma/public/generic/wrong-region';
import { FooterComponent } from '@rma/public/site/private/orc-footer';
import { SiteHeaderComponent } from '@rma/public/site/private/orc-header';
import { catchError, combineLatest, distinctUntilChanged, filter, map, mergeMap, of, skip } from 'rxjs';
import { LazyLoadThirdPartyScriptsService } from '../util-lazy-load-scripts/lazy-load-scripts';
import { FooterHydrationTriggerService } from './footer-hydration-trigger.service';
@Component({
  selector: 'rma-root',
  templateUrl: './rma-layout.component.html',
  styleUrls: ['./rma-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FooterHydrationTriggerService, LazyLoadThirdPartyScriptsService],
  imports: [AsyncPipe, CompareTrayContainerComponent, FooterComponent, MatProgressSpinnerModule, RouterModule, SiteHeaderComponent],
})
export class RmaLayoutComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly platform = inject(Platform);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly shortlistService = inject(ShortlistService);
  private readonly footerHydrationTrigger = inject(FooterHydrationTriggerService);

  // Resolved here rather than inside hasTray: resolveAgents() creates effects, which a computed
  // cannot do. Memoised, so this joins the tray's own resolution instead of starting a second one.
  private readonly shortlistAgents = this.shortlistService.resolveAgents();

  private readonly routeData$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map(() => this.route),
    map((route) => {
      while (route.firstChild) {
        route = route.firstChild;
      }
      return route;
    }),
    filter((route) => route.outlet === 'primary'),
    mergeMap((route) => route.data),
    catchError(() =>
      of({
        hideFooter: false,
        hideHeader: false,
        basicHeader: false,
        fullWidthHeader: false,
        showMarketingNav: false,
        renownedBranding: false,
        loginHeader: false,
        showCompareTray: false,
      }),
    ),
    rmaShareReplay(),
    takeUntilDestroyed(),
  );

  // The first value matches what the server rendered, so only a later change needs the footer block hydrated.
  private readonly footerVisibilityChanged = toSignal(
    this.routeData$.pipe(
      map(({ hideFooter }) => !hideFooter),
      distinctUntilChanged(),
      skip(1),
      map(() => true),
    ),
    { initialValue: false },
  );

  // angular scrollPositionRestoration can have issues in non blink browsers
  protected readonly scrollSmooth = this.platform.BLINK;

  // Resolved agents rather than shortlist codes: a code that fails to resolve renders no tray, and
  // space reserved against it would never be released.
  protected readonly hasTray = computed(() => this.shortlistAgents.agents().length > 0);

  protected readonly showFooter$ = this.routeData$.pipe(map(({ hideFooter }) => !hideFooter));
  protected readonly footerShouldHydrate = computed(() => this.footerVisibilityChanged() || this.footerHydrationTrigger.footerInView());
  protected readonly showHeader$ = this.routeData$.pipe(map(({ hideHeader }) => !hideHeader));
  protected readonly basicHeader$ = this.routeData$.pipe(map(({ basicHeader }) => !!basicHeader));
  protected readonly fullWidthHeader$ = this.routeData$.pipe(map(({ fullWidthHeader }) => !!fullWidthHeader));
  protected readonly showMarketingNav$ = this.routeData$.pipe(map(({ showMarketingNav }) => !!showMarketingNav));
  protected readonly renownedBranding$ = this.routeData$.pipe(map(({ renownedBranding }) => !!renownedBranding));
  protected readonly loginHeader$ = this.routeData$.pipe(map(({ loginHeader }) => !!loginHeader));
  protected readonly showCompareTray$ = combineLatest([
    this.routeData$.pipe(map(({ showCompareTray }) => !!showCompareTray)),
    this.featureFlagService.getFeature$(TempFeatureFlags.CompareTray, false),
  ]).pipe(map(([routeOptsIn, flagOn]) => routeOptsIn && flagOn));
  protected isLoading = false;

  constructor() {
    const lazyLoad3rdPartyScripts = inject(LazyLoadThirdPartyScriptsService);
    const wrongRegionModalService = inject(WrongRegionModalService);
    const consultationOfferService = inject(ConsultationOfferService);
    const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    afterNextRender(() => {
      lazyLoad3rdPartyScripts.initialize();
      wrongRegionModalService.initialise();
      consultationOfferService.initialise();
      // The footer is still inside a dehydrated block, so a view query cannot reach it yet.
      this.footerHydrationTrigger.observe(host.querySelector('rma-footer'));
    });

    this.router.events.pipe(takeUntilDestroyed()).subscribe((e) => this.setLoading(e));
  }

  // The tray re-emits rather than navigating, so its route belongs to whoever mounts it.
  protected onCompare(): void {
    this.router.navigate(['/account/compare-agents']);
  }

  private setLoading(event: Event): void {
    if (event instanceof NavigationStart) {
      this.isLoading = true;
    } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
      // Set loading state to false in these events to hide the spinner in case a request fails
      this.isLoading = false;
    }
  }
}
